import { createFileRoute, Link } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import {
  listInsightTagsAdmin,
  reorderInsightTags,
  deleteInsightTag,
} from "@/lib/api/insight-tags.functions";
import { DataTable } from "@/components/admin/DataTable";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { InsightsAdminNav } from "@/components/admin/InsightsAdminNav";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { tableActionsColumn, TableRowActions } from "@/components/admin/TableRowActions";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/insights/tags/")({
  loader: () => listInsightTagsAdmin(),
  component: InsightTagsListPage,
});

type Row = Awaited<ReturnType<typeof listInsightTagsAdmin>>[number];

function InsightTagsListPage() {
  const initial = Route.useLoaderData();
  const [rows, setRows] = useState(initial);
  const [busy, setBusy] = useState(false);

  async function persistOrder(next: Row[]) {
    setBusy(true);
    try {
      await reorderInsightTags({ data: { ids: next.map((r) => r.id) } });
      setRows(next);
      toast.success("Order updated");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to reorder");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(row: Row) {
    try {
      await deleteInsightTag({ data: { id: row.id } });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      toast.success("Tag deleted");
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to delete";
      if (message.includes("article(s)")) {
        const confirmed = window.confirm(`${message}\n\nRemove this tag from all articles and delete it?`);
        if (!confirmed) return;
        await deleteInsightTag({ data: { id: row.id, removeFromArticles: true } });
        setRows((prev) => prev.filter((r) => r.id !== row.id));
        toast.success("Tag removed from articles and deleted");
        return;
      }
      toast.error(message);
      throw e;
    }
  }

  const columns: ColumnDef<Row>[] = [
    {
      accessorKey: "name",
      header: "Tag",
      cell: ({ row }) => (
        <span className="font-medium">
          {row.original.name}
          {row.original.featured ? <span className="ml-2 text-xs text-gold">Popular</span> : null}
        </span>
      ),
    },
    { accessorKey: "slug", header: "Slug", meta: { filterVariant: "text" } },
    {
      id: "usageCount",
      header: "Articles",
      accessorFn: (row) => row.usageCount,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      ...tableActionsColumn<Row>(),
      cell: ({ row }) => (
        <TableRowActions
          edit={{ to: "/admin/insights/tags/$id", params: { id: row.original.id } }}
          onDelete={() => handleDelete(row.original)}
          deleteLabel={row.original.name}
        />
      ),
    },
  ];

  return (
    <AdminPageShell>
      <AdminPageHeader
        title="Insight tags"
        action={
          <Button asChild>
            <Link to="/admin/insights/tags/new">
              <Plus className="mr-2 h-4 w-4" /> New tag
            </Link>
          </Button>
        }
      />
      <InsightsAdminNav />
      <p className="mb-4 text-sm text-muted-foreground">
        Manage topic tags for insight cards and the Popular Topics section. Drag rows to reorder. Tags marked Popular appear on the insights page when set.
      </p>
      <DataTable
        tableId="insight-tags"
        columns={columns}
        data={rows}
        enableRowReorder
        onReorder={persistOrder}
        reorderDisabled={busy}
      />
    </AdminPageShell>
  );
}
